/**
 * ApplicationsPage — `/service-desk/applications`.
 *
 * Slice B10: wired to GET /v1/service-desk/applications — real paginated data.
 */
import { type JSX, useState } from 'react';
import { Search } from 'lucide-react';
import { useQuery } from '@tanstack/react-query';

import { listApplications, type ApplicationItem } from '../../lib/api/service-desk';
import { PageHeader } from '../shared/StaffUI';

const STATUS_OPTIONS = [
  '', 'OPEN', 'IN_PROGRESS', 'AWAITING_CUSTOMER', 'ESCALATED', 'RESOLVED', 'CLOSED', 'REJECTED',
];

export function ApplicationsPage(): JSX.Element {
  const [search, setSearch] = useState('');
  const [status, setStatus] = useState('');
  const [page, setPage] = useState(1);

  const { data, isError, error, isPending } = useQuery<{
    readonly items: readonly ApplicationItem[];
    // eslint-disable-next-line upmp/no-number-money -- row count, not money
    readonly total: number;
    readonly page: number;
    readonly pageSize: number;
  }>({
    queryKey: ['service-desk', 'applications', { page, status, search }],
    queryFn: () =>
      listApplications({ page, pageSize: 25, status: status || undefined, search: search || undefined }),
    placeholderData: (prev) => prev,
  });

  return (
    <div className="space-y-6">
      <PageHeader title="Applications" description="Form submissions across all SDAs." />

      <div className="flex items-center gap-3">
        <div className="relative flex-1 max-w-md">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted" />
          <input
            type="search"
            value={search}
            onChange={(e) => { setSearch(e.target.value); setPage(1); }}
            placeholder="Search by case number, title…"
            className="w-full pl-9 pr-3 py-2 border border-border rounded-lg text-sm"
          />
        </div>
        <select
          value={status}
          onChange={(e) => { setStatus(e.target.value); setPage(1); }}
          className="px-3 py-2 border border-border rounded-lg text-sm bg-white"
        >
          {STATUS_OPTIONS.map((s) => (
            <option key={s} value={s}>
              {s || 'All statuses'}
            </option>
          ))}
        </select>
      </div>

      {isError ? (
        <div className="rounded-lg bg-red-50 border border-red-200 p-3 text-xs text-red-900">
          Failed to load applications: {error instanceof Error ? error.message : 'Unknown error'}
        </div>
      ) : null}

      <div className="bg-white border border-border rounded-xl overflow-hidden">
        <table className="w-full text-sm">
          <thead>
            <tr className="bg-gray-50 border-b border-border">
              <th className="px-4 py-3 text-left text-xs font-semibold text-muted">Case #</th>
              <th className="px-4 py-3 text-left text-xs font-semibold text-muted">Title</th>
              <th className="px-4 py-3 text-left text-xs font-semibold text-muted">Form key</th>
              <th className="px-4 py-3 text-left text-xs font-semibold text-muted">Agency</th>
              <th className="px-4 py-3 text-left text-xs font-semibold text-muted">Status</th>
              <th className="px-4 py-3 text-left text-xs font-semibold text-muted">Priority</th>
              <th className="px-4 py-3 text-left text-xs font-semibold text-muted">Opened</th>
            </tr>
          </thead>
          <tbody>
            {isPending ? (
              <tr>
                <td colSpan={7} className="px-4 py-8 text-center text-sm text-muted">
                  Loading…
                </td>
              </tr>
            ) : !data?.items.length ? (
              <tr>
                <td colSpan={7} className="px-4 py-8 text-center text-sm text-muted">
                  No applications found.
                </td>
              </tr>
            ) : (
              data.items.map((item) => (
                <tr key={item.id} className="border-b border-border last:border-0 hover:bg-gray-50">
                  <td className="px-4 py-3 font-mono text-xs">{item.caseNumber}</td>
                  <td className="px-4 py-3 max-w-xs truncate">{item.title}</td>
                  <td className="px-4 py-3 font-mono text-xs text-muted">{item.formKey}</td>
                  <td className="px-4 py-3 text-xs">{item.agency}</td>
                  <td className="px-4 py-3">
                    <span className="px-2 py-0.5 rounded-full text-[10px] font-semibold bg-gray-100 text-gray-700">
                      {item.status}
                    </span>
                  </td>
                  <td className="px-4 py-3 text-xs">{item.priority}</td>
                  <td className="px-4 py-3 text-xs text-muted">
                    {new Date(item.openedAt).toLocaleDateString()}
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>

      {data && data.total > data.pageSize ? (
        <div className="flex items-center justify-between text-sm">
          <p className="text-muted text-xs">
            {data.total} total &middot; page {data.page} of {Math.ceil(data.total / data.pageSize)}
          </p>
          <div className="flex gap-2">
            <button
              type="button"
              disabled={page <= 1}
              onClick={() => setPage((p) => p - 1)}
              className="px-3 py-1.5 border border-border rounded-lg text-xs disabled:opacity-40"
            >
              Previous
            </button>
            <button
              type="button"
              disabled={page >= Math.ceil(data.total / data.pageSize)}
              onClick={() => setPage((p) => p + 1)}
              className="px-3 py-1.5 border border-border rounded-lg text-xs disabled:opacity-40"
            >
              Next
            </button>
          </div>
        </div>
      ) : null}
    </div>
  );
}
